import { getLanguage } from './translations';

/**
 * Get currency symbol based on current language
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = () => {
  const lang = getLanguage();
  switch (lang) {
    case 'tr':
      return '₺';
    case 'fr':
      return '€';
    case 'en':
    default:
      return '$';
  }
};

/**
 * Get locale based on current language for number formatting
 * @returns {string} Locale string
 */
export const getLocale = () => {
  const lang = getLanguage();
  switch (lang) {
    case 'tr':
      return 'tr-TR';
    case 'fr':
      return 'fr-FR';
    case 'en':
    default:
      return 'en-US';
  }
};

/**
 * Format currency with locale-specific number format
 * Turkish format: 1.234,56 (thousands separator: dot, decimal separator: comma)
 * English format: 1,234.56 (thousands separator: comma, decimal separator: dot)
 * French format: 1 234,56 (thousands separator: space, decimal separator: comma)
 * 
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency symbol (optional, uses language-based default if not provided)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = null, decimals = 2) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    const defaultCurrency = currency || getCurrencySymbol();
    return `${defaultCurrency}0${decimals > 0 ? ',00' : ''}`;
  }

  // Convert to number if string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Get currency symbol and locale based on current language
  const currencySymbol = currency || getCurrencySymbol();
  const locale = getLocale();

  // Format with locale-specific format
  const formatted = numAmount.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  return `${currencySymbol}${formatted}`;
};

/**
 * Format currency for display (without currency symbol, just number)
 * 
 * @param {number} amount - The amount to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted number string
 */
export const formatNumber = (amount, decimals = 2) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0,00';
  }

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  const locale = getLocale();

  return numAmount.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};
